import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {MaterializeAgent} from "../../api/agent/materializeAgent";

@Injectable({
    providedIn: "root",
})
export class MaterializeAgentPlate {

    constructor(private httpClient: HttpClient) {
    }

    listPlates(token: string): Promise<MaterializePlatesResponse> {
        return this.httpClient.get<MaterializePlatesResponse>(MaterializeAgent.API_BASE_URL + '/v1/plates',{
            headers: new HttpHeaders({
                Authorization: `Bearer ${token}`
            })
        })
            .toPromise();
    }

    startPlateStaticRecording(name: string, token: string, plateId: number,
                              nbFrames: number): Promise<void> {
        return this.startPlateRecording(name, token, plateId, {
            name: 'static',
            nbFrames
        });
    }

    startPlateDynamicRecording(name: string, token: string, plateId: number): Promise<void> {
        return this.startPlateRecording(name, token, plateId, {
            name: 'dynamic'
        });
    }


    private startPlateRecording(name: string, token: string, plateId: number,
                                protocol: any): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.httpClient.post<void>(MaterializeAgent.API_BASE_URL + '/v1/pressuremeasurements/' + name, {}, {
                headers: new HttpHeaders({
                    Authorization: `Bearer ${token}`
                })
            })
                .toPromise()
                .catch((e) => reject(e))
                .then(() => {
                    this.httpClient.post<void>(MaterializeAgent.API_BASE_URL + '/v1/plates/' + plateId + '/start', {
                        name,
                        stream: true,
                        trigger: false,
                        protocol
                    }, {
                        headers: new HttpHeaders({
                            Authorization: `Bearer ${token}`
                        })
                    }).toPromise()
                        .then(() => resolve())
                        .catch((err) => reject(err));
                })
        });
    }

    fetchFootPressureImage(token: string, name: string, index: number, plate: MaterializePlate): Promise<MaterializePlateFootPicture>{
        return new Promise((resolve, reject) => {
            this.fetchFootPressureData(token, name, index, 'Image', plate)
                .catch((e) => reject(e))
                .then((value: MaterializePlateFootData) => {
                    resolve({
                        common: value.common,
                        picture: value.picture
                    });
                })
        })
    }

    /* fetchPressureMeasurements(token: string, name: string, plate: MaterializePlate): Promise<MaterializePlateFootPicture>{
        return new Promise((resolve, reject) => {
            this.fetchFootPressureData(token, name, index, 'Image', plate)
                .catch((e) => reject(e))
                .then((value: MaterializePlateFootData) => {
                    resolve({
                        common: value.common,
                        picture: value.picture
                    });
                })
        })
    }*/

    fetchFootPressureRawData(token: string, name: string, index: number, plate: MaterializePlate): Promise<MaterializePlateFootRawData>{
        return new Promise((resolve, reject) => {
            this.fetchFootPressureData(token, name, index, 'Pressure', plate)
                .catch((e) => reject(e))
                .then((value: MaterializePlateFootData) => {
                    resolve({
                        common: value.common,
                        frames: value.frames
                    });
                })
        })
    }

    fetchFrames(token: string, name: string, plate: MaterializePlate): Promise<MaterializePlateStaticRecording> {
        return new Promise((resolve, reject) => {
            const headers = new HttpHeaders({
                'Accept': 'multipart/mixed',
                Authorization: `Bearer ${token}`
            });

            this.httpClient.get(MaterializeAgent.API_BASE_URL + '/v1/pressuremeasurements/' + name + '/frames/', {
                headers,
                responseType: 'arraybuffer', observe: 'response'
            })
                .toPromise()
                .then((response => {
                    const contentType = response.headers.get('Content-Type');
                    // console.log(contentType);
                    if (!contentType || !contentType.includes('boundary=')) {
                        throw new Error('Boundary not found in response Content-Type header');
                    }

                    // Split parts
                    const boundary = contentType.split('boundary=')[1]
                    const parts: ArrayBuffer[] = this.parseMultipart(response.body as ArrayBuffer, boundary);
                    if (parts.length < 2) {
                        throw new Error('Rest multipart response must have at least 2 body parts');
                    }

                    // Read JSON first part
                    const decoder = new TextDecoder('utf-8');
                    const rawString = decoder.decode(parts[0]);
                    const jsonStartIndex = rawString.indexOf('\r\n\r\n') + 4; // Trouve la fin des en-têtes
                    const jsonString = rawString.substring(jsonStartIndex).trim();

                    const recording: MaterializePlateStaticRecording = JSON.parse(jsonString);
                    recording.frames = this.readFramesFromPart(parts[1], recording.frameWidth, recording.frameHeight, recording.nbFrames);
                    recording.plate = plate;
                    resolve(recording);

                }))
                .catch((e) => reject(e));
        });
    }

    private fetchFootPressureData(token: string, name: string, index: number,
                                  format: 'Pressure' | 'Image',
                                  plate: MaterializePlate): Promise<MaterializePlateFootData>{
        return new Promise((resolve, reject) => {
            const headers = new HttpHeaders({
                'format': format,
                'Accept': 'multipart/mixed',
                Authorization: `Bearer ${token}`
            });
            this.httpClient.get(MaterializeAgent.API_BASE_URL + '/v1/pressuremeasurements/' + name + '/feet/' + index, { headers,
                responseType: 'arraybuffer', observe: 'response'})
                .toPromise()
                .then((response) => {
                    const contentType = response.headers.get('Content-Type');
                    // console.log(contentType);
                    if (!contentType || !contentType.includes('boundary=')) {
                        throw new Error('Boundary not found in response Content-Type header');
                    }

                    // Split parts
                    const boundary = contentType.split('boundary=')[1]
                    const parts: ArrayBuffer[] = this.parseMultipart(response.body as ArrayBuffer, boundary);
                    if (parts.length < 2) {
                        throw new Error('Rest multipart response must have at least 2 body parts');
                    }

                    const foot: MaterializePlateFoot = this.readJsonFromPart(parts[0]);
                    let picture: Blob = undefined;
                    let frames: MaterializePlateFrame[] = [];
                    if (format === 'Image') {
                        picture = this.readImageFromPart(parts[1]);
                    } else {
                        frames = this.readFramesFromPart(parts[1], foot.width, foot.height, foot.nbFrames);
                    }
                    resolve({
                        common: {
                            plate: plate.summary,
                            measurementName: name,
                            height: foot.height,
                            width: foot.width,
                            type: foot.type,
                            index: foot.index,
                            nbFrames: foot.nbFrames,
                            durationInMillis: foot.nbFrames / plate.summary.frameRate * 1000,
                            x: foot.x,
                            y: foot.y,
                            orientation: foot.orientation,
                        },
                        picture,
                        frames
                    });
                })
                .catch((e) => reject(e));
        })
    }

    private readJsonFromPart(part: ArrayBuffer): any {
        const decoder = new TextDecoder('utf-8');
        const rawString = decoder.decode(part);
        const jsonStartIndex = rawString.indexOf('\r\n\r\n') + 4; // Trouve la fin des en-têtes
        const jsonString = rawString.substring(jsonStartIndex).trim();
        return JSON.parse(jsonString);
    }

    private readImageFromPart(part: ArrayBuffer): Blob {
        const decoder = new TextDecoder('utf-8');
        const rawPart2 = decoder.decode(part);
        const binaryStartIndex = rawPart2.indexOf('\r\n\r\n') + 4;
        const match = rawPart2.match(/Content-Length:\s*(\d+)/);
        if (!match) {
            throw new Error('No content length');
        }
        if (binaryStartIndex < 4) {
            throw new Error('Impossible de localiser les en-têtes dans la partie binaire.');
        }
        let binaryContent = part.slice(binaryStartIndex);
        binaryContent = binaryContent.slice(0, binaryContent.byteLength);
        return new Blob([binaryContent], { type: 'image/png' });
    }

    private readFramesFromPart(part: ArrayBuffer, width: number, height: number, nbFrames: number): MaterializePlateFrame[]{
        const frames: MaterializePlateFrame[] = [];

        const rawPart =  new TextDecoder('utf-8').decode(part);
        const binaryStartIndex = rawPart.indexOf('\r\n\r\n') + 4;
        const match = rawPart.match(/Content-Length:\s*(\d+)/);
        if (!match) {
            throw new Error('No content length');
        }
        if (binaryStartIndex < 4) {
            throw new Error('Impossible de localiser les en-têtes dans la partie binaire.');
        }
        let binaryContent = part.slice(binaryStartIndex);
        const size = 4 * height * width * nbFrames;

        if (binaryContent.byteLength > size) {
            binaryContent = binaryContent.slice(0, size);
        }
        const floatArray = new Float32Array(binaryContent);

        if (floatArray.length % (nbFrames * width * height) !== 0) {
            throw new Error(`Not a correct number of pressure data. Nb: ${floatArray.length}, width: ${width}, height: ${height}, nb frames: ${nbFrames}`);
        }

        for(let frameIndex = 0; frameIndex < nbFrames; frameIndex++) {
            const frame = new MaterializePlateFrame();
            const middle = frameIndex === Math.round(frames.length / 2);
            // if (middle) console.log('----------');
            for (let row = 0; row < height; row++) {
                const rowData: number[] = [];
                let colStr = '';
                for (let col = 0; col < width; col++) {
                    // Calcul de l'index global dans le tableau unidimensionnel
                    const index = frameIndex * width * height + row * width + col;
                    rowData.push(floatArray[index]);
                    colStr += floatArray[index] + ', ';
                }
                // if (middle) console.log(colStr);
                frame.data.push(rowData);
            }
            frames.push(frame);
        }
        return frames;
    }

    private parseMultipart(response: ArrayBuffer, boundary: string): ArrayBuffer[] {
        const boundaryBytes = new TextEncoder().encode(`--${boundary}`);
        const responseBytes = new Uint8Array(response);

        let start = 0;
        let parts: ArrayBuffer[] = [];

        for (let i = 0; i < responseBytes.length; i++) {
            if (this.isBoundary(responseBytes, i, boundaryBytes)) {
                if (i > start) {
                    // Extract part between start and boundary
                    const part = responseBytes.slice(start, i).buffer;
                    parts.push(part);
                }
                start = i + boundaryBytes.length;
            }
        }

        // Handle final part
        if (start < responseBytes.length) {
            parts.push(responseBytes.slice(start).buffer);
        }

        parts = parts.filter(value => value?.byteLength > 10);
        // console.log(['parts', parts]);
        return parts;
    }

    isBoundary(buffer: Uint8Array, position: number, boundary: Uint8Array): boolean {
        for (let j = 0; j < boundary.length; j++) {
            if (buffer[position + j] !== boundary[j]) {
                return false;
            }
        }
        return true;
    }
}

export class MaterializePlatesResponse {
    plates: MaterializePlateResponse[];
}

export class MaterializePlateResponse {
    state: 'measuring' | 'ready';
    id: number;
    brand: string;
    firmware: string;
    licensed: string;
    model: string;
    serial: string;
    width: number;
    height: number;
    sensorWidth: number;
    sensorHeight: number;
    frameRate: number;
}

export class MaterializePlateSummary {
    id: number;
    brand: string;
    firmware: string;
    licensed: string;
    model: string;
    serial: string;
    width: number;
    height: number;
    sensorWidth: number;
    sensorHeight: number;
    frameRate: number;

}

export class MaterializePlate {
    state: 'measuring' | 'ready';
    summary: MaterializePlateSummary;
}

export class MaterializePlateFoot {
    nbFrames: number;
    orientation: 'north' | 'south';
    firstFrame: number;
    width: number;
    x: number;
    index: number;
    y: number;
    type: 'left' | 'right';
    height: number;
}

class MaterializePlateFootData {
    common: MaterializePlateFootCommonData;
    frames: MaterializePlateFrame[] = [];
    picture: Blob;
}

export class MaterializePlateFootRawData {
    frames: MaterializePlateFrame[] = [];
    common: MaterializePlateFootCommonData;
}

export class MaterializePlateFootPicture {
    picture: Blob;
    common: MaterializePlateFootCommonData;
}

export class MaterializePlateFootCommonData {
    index: number;
    type: 'left' | 'right';
    width: number;
    height: number;
    measurementName: string;
    nbFrames: number;
    durationInMillis: number;
    x: number;
    y: number;
    orientation: 'north' | 'south';
    plate: MaterializePlateSummary;
}

export class MaterializePlateFrame {
    data: number[][] = [];
}

export class MaterializePlateStreamingFrame {
    fragments: MaterializePlateDataPressureFragment[] = [];
    measurementName: string;
}


export class MaterializePlateStaticRecording {
    frameWidth: number;
    frameHeight: number;
    nbFramesInMeasurement: number;
    startFrame: number;
    nbFrames: number;
    frames: MaterializePlateFrame[];
    plate: MaterializePlate;
}


export class MaterializePlateDataPressureFragment {
    x: number;
    y: number;
    width: number;
    height: number;
    data: number[][];
    plate: MaterializePlate;
}

